import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Package, Plus, Loader2, Pencil, Trash2, Play, Pause, RefreshCw, CalendarClock } from 'lucide-react';
import { formatCurrency, formatDateShort } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Prestations() {
  const [prestations, setPrestations] = useState([]);
  const [recurrences, setRecurrences] = useState([]);
  const [clients, setClients] = useState([]);
  const [unites, setUnites] = useState([]);
  const [periodicites, setPeriodicites] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prestation dialog
  const [prestOpen, setPrestOpen] = useState(false);
  const [editingPrest, setEditingPrest] = useState(null);
  const [prestForm, setPrestForm] = useState({ reference: '', designation: '', description: '', prix_unitaire_ht: 0, unite: 'unité', taux_tva: 20, compte_comptable: '706000' });
  // Recurrence dialog
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState({ client_id: '', designation: '', periodicite: 'mensuel', date_debut: new Date().toISOString().split('T')[0], date_fin: '', lignes: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/prestations`),
      axios.get(`${API_URL}/prestations/recurrences`),
      axios.get(`${API_URL}/clients`),
      axios.get(`${API_URL}/prestations/unites`),
      axios.get(`${API_URL}/prestations/periodicites`),
    ]).then(([p, r, c, u, per]) => {
      setPrestations(p.data); setRecurrences(r.data); setClients(c.data); setUnites(u.data); setPeriodicites(per.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const [p, r] = await Promise.all([
      axios.get(`${API_URL}/prestations`), axios.get(`${API_URL}/prestations/recurrences`),
    ]);
    setPrestations(p.data); setRecurrences(r.data);
  };

  // Prestation CRUD
  const savePrest = async () => {
    if (!prestForm.reference || !prestForm.designation) { toast.error('Référence et désignation requises'); return; }
    setSaving(true);
    try {
      if (editingPrest) { await axios.put(`${API_URL}/prestations/${editingPrest}`, prestForm); toast.success('Prestation modifiée'); }
      else { await axios.post(`${API_URL}/prestations`, prestForm); toast.success('Prestation créée'); }
      setPrestOpen(false); setEditingPrest(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const deletePrest = async (id) => {
    try { await axios.delete(`${API_URL}/prestations/${id}`); toast.success('Désactivée'); refresh(); } catch (e) { toast.error('Erreur'); }
  };

  // Recurrence
  const addRecLigne = () => setRecForm({ ...recForm, lignes: [...recForm.lignes, { description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 }] });
  const addPrestLigne = (prest) => setRecForm({ ...recForm, lignes: [...recForm.lignes, { prestation_id: prest.id, description: prest.designation, quantite: 1, prix_unitaire_ht: prest.prix_unitaire_ht, taux_tva: prest.taux_tva }] });
  const removeRecLigne = (i) => setRecForm({ ...recForm, lignes: recForm.lignes.filter((_, idx) => idx !== i) });

  const saveRec = async () => {
    if (!recForm.client_id || !recForm.designation || recForm.lignes.length === 0) { toast.error('Client, désignation et lignes requises'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/prestations/recurrences`, recForm);
      toast.success('Auto-facturation créée');
      setRecOpen(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const toggleRec = async (rec) => {
    try {
      if (rec.is_active) { await axios.post(`${API_URL}/prestations/recurrences/${rec.id}/suspend`); toast.success('Suspendue'); }
      else { await axios.post(`${API_URL}/prestations/recurrences/${rec.id}/reactiver`); toast.success('Réactivée'); }
      refresh();
    } catch (e) { toast.error('Erreur'); }
  };

  const genererManuel = async (recId) => {
    try {
      const res = await axios.post(`${API_URL}/prestations/recurrences/${recId}/generer`);
      toast.success(`Facture ${res.data.numero} générée`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const genererToutes = async () => {
    try {
      const res = await axios.post(`${API_URL}/prestations/recurrences/generer-toutes`);
      toast.success(res.data.message);
      refresh();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold"><Package className="w-6 h-6 inline mr-2" />Prestations & Auto-facturation</h1>
      </div>

      <Tabs defaultValue="prestations">
        <TabsList>
          <TabsTrigger value="prestations"><Package className="w-4 h-4 mr-1" /> Catalogue ({prestations.length})</TabsTrigger>
          <TabsTrigger value="recurrences"><CalendarClock className="w-4 h-4 mr-1" /> Auto-facturation ({recurrences.length})</TabsTrigger>
        </TabsList>

        {/* PRESTATIONS */}
        <TabsContent value="prestations" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingPrest(null); setPrestForm({ reference: '', designation: '', description: '', prix_unitaire_ht: 0, unite: 'unité', taux_tva: 20, compte_comptable: '706000' }); setPrestOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle prestation
            </Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow>
              <TableHead>Réf.</TableHead><TableHead>Désignation</TableHead><TableHead>Prix HT</TableHead><TableHead>Unité</TableHead><TableHead>TVA</TableHead><TableHead>Compte</TableHead><TableHead className="w-24">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {prestations.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-sm">{p.reference}</TableCell>
                  <TableCell className="font-medium">{p.designation}{p.description && <span className="text-xs text-slate-400 ml-2">{p.description}</span>}</TableCell>
                  <TableCell className="font-mono">{formatCurrency(p.prix_unitaire_ht)}</TableCell>
                  <TableCell className="text-sm">{p.unite}</TableCell>
                  <TableCell className="text-sm">{p.taux_tva}%</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{p.compte_comptable}</TableCell>
                  <TableCell><div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingPrest(p.id); setPrestForm(p); setPrestOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deletePrest(p.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
              {prestations.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Aucune prestation</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* RECURRENCES */}
        <TabsContent value="recurrences" className="space-y-4">
          <div className="flex justify-between">
            <Button variant="outline" onClick={genererToutes}><RefreshCw className="w-4 h-4 mr-2" /> Générer toutes les factures dues</Button>
            <Button onClick={() => { setRecForm({ client_id: '', designation: '', periodicite: 'mensuel', date_debut: new Date().toISOString().split('T')[0], date_fin: '', lignes: [] }); setRecOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle auto-facturation
            </Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow>
              <TableHead>Client</TableHead><TableHead>Désignation</TableHead><TableHead>Périodicité</TableHead><TableHead>Début</TableHead><TableHead>Fin</TableHead><TableHead>Prochaine</TableHead><TableHead>Générées</TableHead><TableHead>Statut</TableHead><TableHead className="w-28">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recurrences.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.client_name}</TableCell>
                  <TableCell className="text-sm">{r.designation}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{r.periodicite}</Badge></TableCell>
                  <TableCell className="text-sm">{formatDateShort(r.date_debut)}</TableCell>
                  <TableCell className="text-sm">{r.date_fin ? formatDateShort(r.date_fin) : '—'}</TableCell>
                  <TableCell className="text-sm font-medium">{r.prochaine_facture || '—'}</TableCell>
                  <TableCell><Badge variant="outline">{r.total_factures_generees}</Badge></TableCell>
                  <TableCell><Badge className={r.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{r.is_active ? 'Active' : 'Suspendue'}</Badge></TableCell>
                  <TableCell><div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => genererManuel(r.id)} title="Générer maintenant"><Play className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleRec(r)} title={r.is_active ? 'Suspendre' : 'Réactiver'}>{r.is_active ? <Pause className="w-3 h-3 text-amber-500" /> : <Play className="w-3 h-3 text-green-500" />}</Button>
                  </div></TableCell>
                </TableRow>
              ))}
              {recurrences.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Aucune auto-facturation</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Prestation Dialog */}
      <Dialog open={prestOpen} onOpenChange={setPrestOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingPrest ? 'Modifier la prestation' : 'Nouvelle prestation'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Référence *</Label><Input value={prestForm.reference} onChange={e => setPrestForm({...prestForm, reference: e.target.value})} placeholder="PREST-001" /></div>
              <div><Label>Prix unitaire HT *</Label><Input type="number" step="0.01" value={prestForm.prix_unitaire_ht} onChange={e => setPrestForm({...prestForm, prix_unitaire_ht: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div><Label>Désignation *</Label><Input value={prestForm.designation} onChange={e => setPrestForm({...prestForm, designation: e.target.value})} placeholder="Développement web" /></div>
            <div><Label>Description</Label><Input value={prestForm.description || ''} onChange={e => setPrestForm({...prestForm, description: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Unité</Label><Select value={prestForm.unite} onValueChange={v => setPrestForm({...prestForm, unite: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{unites.map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>TVA %</Label><Input type="number" value={prestForm.taux_tva} onChange={e => setPrestForm({...prestForm, taux_tva: parseFloat(e.target.value) || 20})} /></div>
              <div><Label>Compte</Label><Input value={prestForm.compte_comptable || ''} onChange={e => setPrestForm({...prestForm, compte_comptable: e.target.value})} placeholder="706000" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrestOpen(false)}>Annuler</Button>
            <Button onClick={savePrest} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingPrest ? 'Modifier' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurrence Dialog */}
      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle><CalendarClock className="w-5 h-5 inline mr-2" />Nouvelle auto-facturation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Client *</Label><Select value={recForm.client_id} onValueChange={v => setRecForm({...recForm, client_id: v})}><SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Désignation *</Label><Input value={recForm.designation} onChange={e => setRecForm({...recForm, designation: e.target.value})} placeholder="Abonnement mensuel" /></div>
              <div><Label>Périodicité</Label><Select value={recForm.periodicite} onValueChange={v => setRecForm({...recForm, periodicite: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{periodicites.map(p => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              <div />
              <div><Label>Date début *</Label><Input type="date" value={recForm.date_debut} onChange={e => setRecForm({...recForm, date_debut: e.target.value})} /></div>
              <div><Label>Date fin (optionnel)</Label><Input type="date" value={recForm.date_fin} onChange={e => setRecForm({...recForm, date_fin: e.target.value})} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Lignes de facturation</Label>
                <div className="flex gap-2">
                  {prestations.length > 0 && (
                    <Select onValueChange={v => { const p = prestations.find(p => p.id === v); if (p) addPrestLigne(p); }}>
                      <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="+ Prestation" /></SelectTrigger>
                      <SelectContent>{prestations.map(p => <SelectItem key={p.id} value={p.id}>{p.reference} — {p.designation}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Button variant="outline" size="sm" onClick={addRecLigne}><Plus className="w-3 h-3 mr-1" /> Ligne libre</Button>
                </div>
              </div>
              <Table><TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="w-16">Qté</TableHead><TableHead className="w-24">PU HT</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>{recForm.lignes.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><Input value={l.description} onChange={e => { const ls = [...recForm.lignes]; ls[i] = {...ls[i], description: e.target.value}; setRecForm({...recForm, lignes: ls}); }} /></TableCell>
                  <TableCell><Input type="number" value={l.quantite} onChange={e => { const ls = [...recForm.lignes]; ls[i] = {...ls[i], quantite: parseFloat(e.target.value) || 0}; setRecForm({...recForm, lignes: ls}); }} /></TableCell>
                  <TableCell><Input type="number" step="0.01" value={l.prix_unitaire_ht} onChange={e => { const ls = [...recForm.lignes]; ls[i] = {...ls[i], prix_unitaire_ht: parseFloat(e.target.value) || 0}; setRecForm({...recForm, lignes: ls}); }} /></TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => removeRecLigne(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button></TableCell>
                </TableRow>
              ))}</TableBody></Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecOpen(false)}>Annuler</Button>
            <Button onClick={saveRec} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
